<template>
  <div ref="switcher" class="dh-switcher" :class="{ 'is-locked': locked }">
    <div
      class="dh-switcher-trigger"
      :class="{ 'is-locked': locked }"
      @click="handleTriggerClick"
    >
      <img
        v-if="currentItem && currentItem.logo"
        :src="currentItem.logo"
        class="dh-switcher-avatar"
        alt=""
      />
      <div v-else class="dh-switcher-avatar is-default">
        <i class="el-icon-user"></i>
      </div>
      <span class="dh-switcher-name" :title="currentTitle">{{ currentTitle }}</span>
      <i
        class="el-icon-arrow-down dh-switcher-caret"
        :class="{ 'is-open': open }"
      ></i>
    </div>

    <transition name="el-zoom-in-top">
      <div
        v-if="open"
        class="dh-switcher-panel"
        :class="{ 'is-panel-hover': panelHover }"
        @mouseenter="panelHover = true"
        @mouseleave="panelHover = false"
      >
        <template v-for="group in groups">
          <div v-if="group.items.length" :key="group.key" class="dh-switcher-group">
            <div class="dh-switcher-group-title">{{ group.label }}</div>
            <div
              v-for="item in group.items"
              :key="group.key + '-' + item.id + '-' + item.agentName"
              class="dh-switcher-item"
              :class="{ 'is-active': isCurrent(item) }"
              :title="item.title"
              @click="handleSelect(item)"
            >
              <img v-if="item.logo" :src="item.logo" class="dh-item-avatar" alt="" />
              <div v-else class="dh-item-avatar is-default">
                <i class="el-icon-user"></i>
              </div>
              <span class="dh-item-name">{{ item.title }}</span>
              <i v-if="isCurrent(item)" class="el-icon-check dh-item-check"></i>
            </div>
          </div>
        </template>
        <div v-if="!hasAnyItem" class="dh-switcher-empty">暂无可切换的数字人</div>
      </div>
    </transition>

    <div v-if="locked && !open" class="dh-switcher-lock-tip">
      会话进行中，结束后可切换
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { parseAgentTags } from '@/store/modules/cosmoClaw'
import { getAgentRouteName } from '../mixins/workspaceConstants'

export default {
  name: 'DigitalHumanSwitcher',
  data() {
    return {
      open: false,
      // 面板是否处于鼠标悬浮状态，用于控制滚动条显隐
      panelHover: false
    }
  },
  computed: {
    ...mapState('user', ['agentList']),
    ...mapState('cosmoClaw', ['currentAgentId', 'currentAgentName', 'isStreaming']),
    // 会话进行中（流式输出）时禁止切换，会话结束后才允许
    locked() {
      return !!this.isStreaming
    },
    // 可切换列表：数字人与智能体都在其中，过滤未启用/已删除
    switchableList() {
      const list = this.agentList || []
      return list
        .filter((a) => a.enabled !== false && a.deleted === 0)
        .map((a) => {
          const { type, internalName, isDemo, isGc, isLqz } = parseAgentTags(a.tags, a.name)
          if ((type !== 'digital-human' && type !== 'agent') || !internalName) return null
          return {
            id: a.id,
            agentId: String(a.id || a.agentId || ''),
            agentName: internalName,
            type,
            title: a.displayName || a.name || '',
            logo: a.icon || '',
            isDemo: !!isDemo,
            isGc: !!isGc,
            isLqz: !!isLqz
          }
        })
        .filter(Boolean)
    },
    digitalHumans() {
      return this.switchableList.filter((i) => i.type === 'digital-human')
    },
    agents() {
      return this.switchableList.filter((i) => i.type === 'agent')
    },
    groups() {
      return [
        { key: 'dh', label: '数字人团队', items: this.digitalHumans },
        { key: 'agent', label: '智能应用', items: this.agents }
      ]
    },
    hasAnyItem() {
      return this.digitalHumans.length > 0 || this.agents.length > 0
    },
    // 当前数字人/智能体：优先按真实 agentId 匹配（支持同 internalName 多实例）
    currentItem() {
      const list = this.switchableList
      if (this.currentAgentId) {
        const byId = list.find((i) => i.agentId && i.agentId === String(this.currentAgentId))
        if (byId) return byId
      }
      if (this.currentAgentName) {
        return list.find((i) => i.agentName === this.currentAgentName) || null
      }
      return null
    },
    currentTitle() {
      return (this.currentItem && this.currentItem.title) || '选择数字人'
    }
  },
  watch: {
    currentAgentId() {
      this.open = false
    },
    open(val) {
      if (!val) this.panelHover = false
    }
  },
  mounted() {
    document.addEventListener('mousedown', this._handleOutsideClick, true)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this._handleOutsideClick, true)
  },
  methods: {
    _handleOutsideClick(e) {
      if (!this.open) return
      if (this.$el && this.$el.contains(e.target)) return
      this.open = false
    },
    handleTriggerClick() {
      if (this.locked) return
      this.open = !this.open
    },
    isCurrent(item) {
      const cur = this.currentItem
      if (!cur) return false
      if (cur.id != null && item.id != null) return String(cur.id) === String(item.id)
      return cur.agentName === item.agentName
    },
    // 切换数字人/智能体（与左侧菜单点击行为对齐）：
    // - 智能体：与 handleAgentClick 一致，跳转该智能体绑定的页面路由（如 agent/indicator），
    //   并在此处 dispatch switchAgent + createNewSession（目标页面无 SessionChatPage 的 URL 同步链路）
    // - 数字人：与 handleNewChatClick 一致，回到会话初始页 SmartEngineChat，
    //   由 SessionChatPage._syncFromQuery 完成 switchAgent + createNewSession
    // - 智能体无绑定路由时兜底回到会话初始页
    handleSelect(item) {
      if (this.locked || !item || !item.agentName) return
      this.open = false
      if (this.isCurrent(item)) return
      const agentId = item.agentId || item.agentName
      if (item.type === 'agent') {
        const routeName = getAgentRouteName(item.agentName)
        if (routeName) {
          this.$store.commit('cosmoClaw/SET_LOCAL_DEMO_MODE', false)
          this.$store.dispatch('cosmoClaw/switchAgent', { agentName: item.agentName, agentId })
          this.$store.dispatch('cosmoClaw/createNewSession', {
            agentName: item.agentName,
            agentId
          })
          const nav = this.$router.push({ name: routeName, query: { agentId }})
          if (nav && typeof nav.catch === 'function') nav.catch(() => {})
          return
        }
      }
      const nav = this.$router.replace({
        name: 'SmartEngineChat',
        query: { agentId, new: '1' }
      })
      if (nav && typeof nav.catch === 'function') nav.catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.dh-switcher {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.dh-switcher-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 8px 0 4px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    // background: #f2f3f5;
  }

  &.is-locked {
    cursor: not-allowed;

    &:hover {
      background: transparent;
    }
  }
}

.dh-switcher-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;

  &.is-default {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #4a5ff3;
    color: #fff;
    font-size: 14px;
  }
}

.dh-switcher-name {
  max-width: 180px;
  font-size: 14px;
  font-weight: 500;
  color: #1f2329;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dh-switcher-caret {
  font-size: 12px;
  color: #1f2329;
  transition: transform 0.2s ease;

  &.is-open {
    transform: rotate(180deg);
  }
}

.dh-switcher-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 3000;
  width: 240px;
  max-height: 360px;
  overflow-y: auto;
  padding: 6px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  scrollbar-color: auto;

  &::-webkit-scrollbar {
    width: 10px !important;
    height: 10px !important;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
    box-shadow: none;
    border: none;
  }
  /* 轨道 10px，滑块视觉 8px：1px 透明边框把两侧各收进 1px，避免紧贴内容区 */
  &::-webkit-scrollbar-thumb {
    background-color: transparent !important;
    background-clip: padding-box !important;
    border: 1px solid transparent;
    border-radius: 5px !important;
    box-shadow: none !important;
  }

  /* 悬浮时显示滚动条。由 JS 维护 class，规避 CSS :hover 在滚动到端点时的粘滞 */
  &.is-panel-hover::-webkit-scrollbar-thumb {
    background-color: rgba(156, 167, 248, 0.2) !important;
    background-clip: padding-box !important;
    border: 1px solid transparent;
    border-radius: 5px !important;
  }
}
.dh-switcher-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dh-switcher-group-title {
  padding: 6px 8px 4px;
  font-size: 12px;
  color: #8f959e;
  line-height: 18px;
}

.dh-switcher-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f2f3f5;
  }

  &.is-active {
    background: #eef1ff;

    .dh-item-name {
      color: #4a5ff3;
    }
  }
}

.dh-item-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;

  &.is-default {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #4a5ff3;
    color: #fff;
    font-size: 12px;
  }
}

.dh-item-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #1f2329;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dh-item-check {
  font-size: 12px;
  color: #4a5ff3;
  flex-shrink: 0;
}

.dh-switcher-empty {
  padding: 16px 8px;
  font-size: 12px;
  color: #8f959e;
  text-align: center;
}

/* 会话进行中的锁定提示（悬浮在触发器下方） */
.dh-switcher-lock-tip {
  display: none;
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 3000;
  padding: 4px 8px;
  font-size: 12px;
  color: #fff;
  background: rgba(31, 35, 41, 0.85);
  border-radius: 4px;
  white-space: nowrap;
}

.dh-switcher.is-locked:hover .dh-switcher-lock-tip {
  display: block;
}
</style>
